import { normalizeLinkInBioUrl } from './normalizeLinkInBioUrl';
import { LINK_IN_BIO_LIMITS } from './types';
import type {
  LinkInBioBlock,
  LinkInBioEventSummary,
  LinkInBioItem,
  LinkInBioPageSummary,
  LinkInBioPaths,
} from './types';

export function isLinkInBioEventOver(event: LinkInBioEventSummary | null, now: Date): boolean {
  const end = event?.endDate ?? event?.startDate;
  if (!end) return false;
  return new Date(end).getTime() < now.getTime();
}

export type LinkInBioBlockSchedule = 'scheduled' | 'showing' | 'ended';

export function getLinkInBioBlockSchedule(
  block: Pick<LinkInBioBlock, 'showFrom' | 'showUntil'>,
  now: Date,
): LinkInBioBlockSchedule {
  const time = now.getTime();
  if (block.showFrom && time < new Date(block.showFrom).getTime()) return 'scheduled';
  if (block.showUntil && time >= new Date(block.showUntil).getTime()) return 'ended';
  return 'showing';
}

function isShowable(
  page: LinkInBioPageSummary | undefined,
  now: Date,
): page is LinkInBioPageSummary {
  if (!page || page.status !== 'published' || page.testmode) return false;
  return !(page.type === 'event' && isLinkInBioEventOver(page.event, now));
}

type ResolveInput = {
  blocks: LinkInBioBlock[];
  pages: Record<string, LinkInBioPageSummary>;
  upcomingEvents?: LinkInBioPageSummary[];
  now: Date;
  paths: LinkInBioPaths;
  formatEventDate?: (event: LinkInBioEventSummary) => string | null;
};

export function resolveLinkInBioItems({
  blocks,
  pages,
  upcomingEvents = [],
  now,
  paths,
  formatEventDate,
}: ResolveInput): LinkInBioItem[] {
  const shownPageIds = new Set<string>();
  const items: LinkInBioItem[] = [];

  const toPageItem = (
    page: LinkInBioPageSummary,
    key: string,
    style: LinkInBioBlock['style'],
    title?: string | null,
    icon?: string | null,
  ): LinkInBioItem => ({
    kind: 'page',
    key,
    title: title?.trim() || page.title,
    href: `${paths.pagePrefix}${encodeURIComponent(page.slug)}${paths.pageQuery ? `?${paths.pageQuery}` : ''}`,
    pageType: page.type,
    imageUrl: page.imageUrl,
    icon: icon || null,
    event: page.event ? { ...page.event, dateLabel: formatEventDate?.(page.event) ?? null } : null,
    style,
  });

  blocks.forEach((block, index) => {
    if (block.hidden || getLinkInBioBlockSchedule(block, now) !== 'showing') return;
    const key = block.id || `i${index}`;
    const style = block.style || 'button';

    switch (block.type) {
      case 'heading': {
        const title = block.title?.trim();
        if (title) items.push({ kind: 'heading', key, title });
        return;
      }
      case 'link': {
        const title = block.title?.trim();
        const href = normalizeLinkInBioUrl(block.url);
        if (!title || !href) return;
        const imageUrl = block.thumbnail?.url || null;
        items.push({
          kind: 'link',
          key,
          title,
          href,
          imageUrl,
          icon: block.icon || null,
          style: imageUrl ? style : 'button',
        });
        return;
      }
      case 'portal':
        items.push({
          kind: 'portal',
          key,
          title: block.title?.trim() || null,
          href: paths.portal,
          icon: block.icon || null,
          style: 'button',
        });
        return;
      case 'email':
        items.push({
          kind: 'email',
          key,
          blockId: block.id ?? null,
          title: block.title?.trim() || null,
        });
        return;
      case 'page': {
        const page = block.page ? pages[block.page] : undefined;
        if (!isShowable(page, now)) return;
        shownPageIds.add(page.id);
        items.push(toPageItem(page, key, style, block.title, block.icon));
        return;
      }
      case 'events': {
        const limit = block.limit ?? LINK_IN_BIO_LIMITS.eventsDefault;
        let added = 0;
        for (const event of upcomingEvents) {
          if (added >= limit) break;
          if (shownPageIds.has(event.id) || !isShowable(event, now)) continue;
          shownPageIds.add(event.id);
          items.push(toPageItem(event, `${key}:${event.id}`, style));
          added += 1;
        }
        return;
      }
      default:
        return;
    }
  });

  return items.filter(
    (item, index) =>
      item.kind !== 'heading' || (items[index + 1] && items[index + 1].kind !== 'heading'),
  );
}
